import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdSearch, MdCheckCircle, MdCancel, MdInbox, MdVisibility, MdDarkMode, MdLightMode, MdEdit } from 'react-icons/md';
import ReasonModal from '../components/ReasonModal';
import ImageCropper from '../components/ImageCropper';
import DragDropImageUpload from '../components/DragDropImageUpload';
import WebPreviewModal from '../components/WebPreviewModal';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDateTime, formatINR, statusBadgeClass, statesMatch, isAllIndiaState, isInternationalState, displayState, ALL_INDIA_STATE, INTERNATIONAL_STATE } from '../utils/format';

const TABS = ['pending', 'accepted', 'rejected', 'all'];

export default function ContributorReview() {
  const [tab, setTab] = useState('pending');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reasons, setReasons] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Queue navigation: the list is browsed one item at a time (like a review
  // queue) instead of via a table + modal. `current` is derived from
  // `list[currentIndex]` rather than kept as separate state, so accepting/
  // rejecting/editing an item and re-fetching the list can never leave a
  // stale copy behind.
  const [currentIndex, setCurrentIndex] = useState(0);
  // 'start' | 'end' | null — which edge of a newly-loaded page (after Next/
  // Previous crosses a page boundary) the queue should land on once that
  // page's list arrives.
  const [pendingEdge, setPendingEdge] = useState(null);
  const [showQueueList, setShowQueueList] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Dark mode is local to this page (not app-wide) — remembered per browser.
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('cr_dark_mode') === '1');
  useEffect(() => {
    localStorage.setItem('cr_dark_mode', darkMode ? '1' : '0');
  }, [darkMode]);
  // Quick approve: when on, Accept skips the amount modal and immediately
  // credits this fixed amount instead. Also remembered per browser.
  const [quickApprove, setQuickApprove] = useState(() => localStorage.getItem('cr_quick_approve') === '1');
  const [defaultAmount, setDefaultAmount] = useState(() => localStorage.getItem('cr_default_amount') || '5');
  useEffect(() => {
    localStorage.setItem('cr_quick_approve', quickApprove ? '1' : '0');
  }, [quickApprove]);
  useEffect(() => {
    localStorage.setItem('cr_default_amount', defaultAmount);
  }, [defaultAmount]);
  const [acceptFor, setAcceptFor] = useState(null);
  const [amount, setAmount] = useState('');
  const [rejectFor, setRejectFor] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateResolutionAction, setDuplicateResolutionAction] = useState(null);
  const [viewMatchedArticle, setViewMatchedArticle] = useState(null);

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: '',
    matter: '',
    category: '',
    state: '',
    city: '',
    is_breaking: false,
    is_featured: false,
    zone_only: true,
    media_type: 'image',
    image_url: '',
    video_url: '',
    thumbnail_url: '',
  });
  const [editUploading, setEditUploading] = useState(false);
  const [editCropper, setEditCropper] = useState({ active: false, image: null, type: null });
  const [showWebPreview, setShowWebPreview] = useState(false);

  const handlePreAccept = async (submission) => {
    setAcceptFor(submission);
    setAmount(submission?.status === 'accepted' ? String(submission.earning_amount != null ? Number(submission.earning_amount) : '') : '');
  };

  // Shared by the amount-modal flow (accept()) and Quick Approve (skips the
  // modal entirely and credits the saved default amount immediately).
  const doAccept = async (submissionId, amt) => {
    setBusy(true);
    try {
      const res = await api.patch(`/admin/contributor-submissions/${submissionId}/accept`, { earning_amount: amt });
      toast.success(res.data?.message || (acceptFor?.status === 'accepted' ? 'Article worth updated' : 'Accepted & published'));
      setAcceptFor(null); setAmount('');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const handleAcceptClick = (submission) => {
    if (quickApprove) {
      const amt = Number(defaultAmount);
      if (!(amt >= 0)) return toast.error('Set a valid default amount first');
      doAccept(submission.id, amt);
    } else {
      handlePreAccept(submission);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/contributor-submissions', {
        params: { status: tab, search: search.trim() || undefined, page, limit },
      });
      setList(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [tab, search, page, limit]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const current = list[currentIndex] || null;

  // Inline title/matter editing directly on the read-only detail card — no
  // need to open the full "Edit" form just to fix a headline or a typo.
  // Drafts re-sync whenever the queue moves to a different submission; a
  // save fires on blur (or Enter for the single-line title).
  const [titleDraft, setTitleDraft] = useState('');
  const [matterDraft, setMatterDraft] = useState('');
  const [savingField, setSavingField] = useState(null); // 'title' | 'matter' | null
  const matterRef = useRef(null);

  useEffect(() => {
    setTitleDraft(current?.title || '');
    setMatterDraft(current?.matter || '');
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (matterRef.current) {
      matterRef.current.style.height = 'auto';
      matterRef.current.style.height = `${matterRef.current.scrollHeight}px`;
    }
  }, [matterDraft]);

  const saveInlineField = async (field, value) => {
    if (!current || value === (current[field] || '')) return;
    setSavingField(field);
    try {
      const res = await api.patch(`/admin/contributor-submissions/${current.id}`, { [field]: value });
      setList((l) => l.map((s) => (s.id === current.id ? { ...s, ...res.data.data } : s)));
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
      if (field === 'title') setTitleDraft(current.title || '');
      else setMatterDraft(current.matter || '');
    } finally {
      setSavingField(null);
    }
  };

  // Switching tabs/search starts a different queue — always land on its first item.
  useEffect(() => {
    setCurrentIndex(0);
    setEditing(false);
    setSelectedIds([]);
  }, [tab, search]);

  // Runs whenever a fresh page of results lands (page change, or a refetch
  // after accept/reject). If Next/Previous just crossed a page boundary,
  // land on the edge the user was navigating toward; otherwise just keep
  // the current position (so accepting item #3 naturally reveals whatever
  // is now #3 — the next one in the queue) and only clamp if that position
  // no longer exists (e.g. the last item on the page was just handled).
  useEffect(() => {
    if (pendingEdge === 'end') {
      setCurrentIndex(Math.max(0, list.length - 1));
      setPendingEdge(null);
    } else if (pendingEdge === 'start') {
      setCurrentIndex(0);
      setPendingEdge(null);
    } else if (currentIndex >= list.length) {
      setCurrentIndex(Math.max(0, list.length - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const isFirstOverall = currentIndex <= 0 && pagination.page <= 1;
  const isLastOverall = currentIndex >= list.length - 1 && pagination.page >= pagination.pages;

  const goPrev = () => {
    if (!list.length) return;
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else if (pagination.page > 1) {
      setPendingEdge('end');
      setPage(pagination.page - 1);
    }
  };

  const goNext = () => {
    if (!list.length) return;
    if (currentIndex < list.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (pagination.page < pagination.pages) {
      setPendingEdge('start');
      setPage(pagination.page + 1);
    }
  };

  const jumpTo = (idx) => {
    setCurrentIndex(idx);
    setShowQueueList(false);
    setEditing(false);
  };

  useEffect(() => {
    api.get('/admin/contributors/meta/rejection-reasons')
      .then((res) => setReasons(res.data.data?.submission || []))
      .catch(() => { });
    // Dropdown sources for the edit form (same as PublishArticle).
    api.get('/categories').then((r) => setCategories((r.data.data || []).map((c) => c.name))).catch(() => { });
    api.get('/cities').then((r) => setCities((r.data.data || []).map((c) => ({ id: c.id, name: c.name, slug: c.slug, state: c.state, name_en: c.name_en })))).catch(() => { });
  }, []);

  const startEdit = (s) => {
    const rawCity = (s.city || s.contributor?.city || '').trim();
    const rawState = (s.state || s.contributor?.state || '').trim();

    const matchingCity = cities.find(c =>
      c.name === rawCity ||
      c.slug === rawCity ||
      (c.name_en && c.name_en.toLowerCase() === rawCity.toLowerCase())
    );

    const canonicalCity = matchingCity ? matchingCity.name : rawCity;
    const effectiveStateRaw = matchingCity?.state || rawState;
    const effectiveState = isAllIndiaState(effectiveStateRaw)
      ? ALL_INDIA_STATE + ' '
      : (isInternationalState(effectiveStateRaw) ? INTERNATIONAL_STATE : effectiveStateRaw);

    setForm({
      title: s.title || '',
      matter: s.matter || '',
      category: s.category || 'General',
      state: effectiveState,
      city: canonicalCity,
      is_breaking: !!s.is_breaking,
      is_featured: !!s.is_featured,
      zone_only: s.zone_only !== false,
      media_type: s.media_type || 'image',
      image_url: s.image_url || '',
      video_url: s.video_url || '',
      thumbnail_url: s.thumbnail_url || '',
    });
    setEditing(true);
  };

  const toggleDetailFlag = async (field) => {
    if (!current) return;
    const nextValue = !current[field];
    try {
      const res = await api.patch(`/admin/contributor-submissions/${current.id}`, { [field]: nextValue });
      setList((l) => l.map((s) => (s.id === current.id ? { ...s, ...res.data.data } : s)));
      toast.success('Updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleEditFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';
      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const selectedFile = files[0];
        if (currentType === 'video') {
          uploadEditToServer(selectedFile, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setEditCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(selectedFile);
        }
      };
      input.click();
      return;
    }

    const file = fileOrType;
    if (type === 'video') {
      uploadEditToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setEditCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleEditCropComplete = async (blob) => {
    const type = editCropper.type;
    setEditCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadEditToServer(file, type);
  };

  const uploadEditToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = type === 'image' ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setEditUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      if (type === 'image') setForm(f => ({ ...f, image_url: url }));
      else if (type === 'thumbnail') setForm(f => ({ ...f, thumbnail_url: url }));
      else setForm(f => ({ ...f, video_url: url }));
      toast.success(`${type} uploaded!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setEditUploading(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const res = await api.patch(`/admin/contributor-submissions/${current.id}`, form);
      setList((l) => l.map((s) => (s.id === current.id ? { ...s, ...res.data.data } : s)));
      setEditing(false);
      toast.success('Submission updated');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally { setBusy(false); }
  };

  const accept = async () => {
    const amt = Number(amount);
    if (!(amt >= 0)) return toast.error('Enter a valid amount');
    doAccept(acceptFor.id, amt);
  };

  const reject = async (reason) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributor-submissions/${rejectFor.id}/reject`, { reason });
      toast.success('Rejected');
      setRejectFor(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectAllOnPage = () => {
    const pendingOnPage = list.filter((s) => s.status === 'pending').map((s) => s.id);
    const allSelected = pendingOnPage.length > 0 && pendingOnPage.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pendingOnPage.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pendingOnPage])));
    }
  };

  const handleBulkReject = async (reason) => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const res = await api.post('/admin/contributor-submissions/bulk-reject', {
        submission_ids: selectedIds,
        reason,
      });
      toast.success(res.data?.message || 'Selected submissions rejected');
      setSelectedIds([]);
      setBulkRejectOpen(false);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reject selected submissions');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`page${darkMode ? ' cr-dark' : ''}`}>
      {/* Tabs, search, and queue position/nav all in one compact row */}
      <div className="review-toolbar">
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => { setTab(t); setPage(1); }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        <div className="review-toolbar-right">
          <label className={`quick-approve-toggle ${quickApprove ? 'active' : ''}`} title="When on, Accept skips the amount prompt and credits this amount immediately">
            <input type="checkbox" checked={quickApprove} onChange={(e) => setQuickApprove(e.target.checked)} />
            <span>Quick approve ₹</span>
            <input
              type="number"
              min="0"
              className="quick-approve-amount"
              value={defaultAmount}
              onChange={(e) => setDefaultAmount(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </label>

          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={selectedIds.length === 0 || busy}
            onClick={() => setBulkRejectOpen(true)}
            title={selectedIds.length === 0 ? 'Select pending articles to reject' : `Reject ${selectedIds.length} selected articles`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, whiteSpace: 'nowrap' }}
          >
            <MdCancel /> Reject All{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </button>

          <button
            className="search-toggle-btn"
            onClick={() => setDarkMode((d) => !d)}
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <MdLightMode /> : <MdDarkMode />}
          </button>

          {searchOpen ? (
            <div className="search-expand-wrap">
              <MdSearch className="search-icon" />
              <input
                autoFocus
                placeholder="Search title…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                onBlur={() => { if (!search) setSearchOpen(false); }}
              />
            </div>
          ) : (
            <button className="search-toggle-btn" onClick={() => setSearchOpen(true)} title="Search">
              <MdSearch />
            </button>
          )}

          {!loading && list.length > 0 && (
            <>
              <span className="queue-position">
                <strong>{(pagination.page - 1) * limit + currentIndex + 1}</strong> of <strong>{pagination.total}</strong>
              </span>
              <button className="btn btn-sm btn-ghost" onClick={goPrev} disabled={isFirstOverall}>‹ Previous</button>
              <button className="btn btn-sm btn-ghost" onClick={goNext} disabled={isLastOverall}>Next ›</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setShowQueueList((s) => !s)}>
                {showQueueList ? 'Hide list' : `Show list (${pagination.total})`}
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : list.length === 0 ? (
        <div className="empty-state">
          <MdInbox className="empty-icon" />
          <h3>Queue empty</h3>
          <p>No “{tab}” submissions.</p>
        </div>
      ) : (
        <>
          {showQueueList && (
            <div className="queue-list">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.02)' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={list.filter(s => s.status === 'pending').length > 0 && list.filter(s => s.status === 'pending').every(s => selectedIds.includes(s.id))}
                    onChange={selectAllOnPage}
                  />
                  <span>Select all on page</span>
                </label>
                {selectedIds.length > 0 && (
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {selectedIds.length} selected
                  </span>
                )}
              </div>
              {list.map((s, idx) => (
                <div key={s.id} className={`queue-list-item ${idx === currentIndex ? 'active' : ''}`} onClick={() => jumpTo(idx)}>
                  {s.status === 'pending' && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(s.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(s.id)}
                      style={{ marginRight: 8, cursor: 'pointer' }}
                    />
                  )}
                  <span className="queue-list-title">
                    {s.title || 'Untitled'}
                    {s.contributor?.is_standard && (
                      <span style={{ marginLeft: 6, fontSize: '11px', color: '#0d8a43', fontWeight: 700 }}>[★ Standard]</span>
                    )}
                  </span>
                  <span className={`badge ${statusBadgeClass(s.status)}`}>{s.status}</span>
                </div>
              ))}
              <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
            </div>
          )}

          {/* Full single-item review card */}
          {current && (
          <div className="dashboard-card">
            {editing ? (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: 0 }}>Edit Submission</h3>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <input
                    type="text"
                    value={titleDraft}
                    onChange={e => setTitleDraft(e.target.value.slice(0, 75))}
                    onBlur={() => saveInlineField('title', titleDraft)}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    disabled={current.status === 'accepted'}
                    placeholder="Untitled"
                    className="inline-edit-field cr-title"
                    style={{ margin: 0, width: '100%', display: 'block', fontFamily: 'Ramabhadra, serif', fontSize: '28px', fontWeight: '400', color: 'var(--text)' }}
                  />
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 2, paddingLeft: 6 }}>
                    {savingField === 'title' ? 'Saving…' : `${titleDraft.length}/75`}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {current.status === 'pending' && (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: selectedIds.includes(current.id) ? 'rgba(239, 68, 68, 0.12)' : 'var(--bg)', fontSize: 12, fontWeight: 600, color: selectedIds.includes(current.id) ? '#dc2626' : 'var(--text)' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(current.id)}
                          onChange={() => toggleSelect(current.id)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span>{selectedIds.includes(current.id) ? 'Selected for bulk reject' : 'Select'}</span>
                      </label>
                    )}
                    {current.category && <span className="badge badge-category">{current.category}</span>}
                    <span className={`badge ${statusBadgeClass(current.status)}`}>{current.status}</span>
                    <span className="badge">{[current.city || current.contributor?.city, current.state || current.contributor?.state].filter(Boolean).join(', ') || 'Not Available'}</span>
                    <span className="cr-meta" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {current.contributor?.name || 'Not Available'}
                      {current.contributor?.is_standard && (
                        <span className="badge" style={{ marginLeft: 6, background: '#e6f7ed', color: '#0d8a43', border: '1px solid #b7ebc7', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                          ★ Standard
                        </span>
                      )}
                    </span>
                    <span className="cr-meta" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{formatDateTime(current.submitted_at)}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    style={{ color: 'var(--primary)', border: '1px solid var(--primary)', padding: '6px 12px', flexShrink: 0 }}
                    onClick={() => setShowWebPreview(true)}
                  >
                    <MdVisibility /> Website Preview
                  </button>
                </div>
              </div>
            )}
            <div>
              {editing ? (
                <>
                  <div className="form-group">
                    <label>Media Type</label>
                    <div className="radio-group">
                      <label className={`radio-card ${form.media_type === 'image' ? 'selected' : ''}`}>
                        <input type="radio" value="image" checked={form.media_type === 'image'}
                          onChange={() => setForm(f => ({ ...f, media_type: 'image' }))} /> Image
                      </label>
                      <label className={`radio-card ${form.media_type === 'video' ? 'selected' : ''}`}>
                        <input type="radio" value="video" checked={form.media_type === 'video'}
                          onChange={() => setForm(f => ({ ...f, media_type: 'video' }))} /> Video
                      </label>
                    </div>
                  </div>

                  {form.media_type === 'image' && (
                    <div className="form-group">
                      <label>Article Image (Web & App)</label>
                      <DragDropImageUpload
                        type="image"
                        uploading={editUploading}
                        imageUrl={form.image_url}
                        onFileSelect={handleEditFileSelect}
                      />
                      <input type="text" value={form.image_url}
                        onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                        placeholder="Or paste image URL" className="mt-sm" />
                    </div>
                  )}

                  {form.media_type === 'video' && (
                    <>
                      <div className="form-group">
                        <label>Video URL</label>
                        <input type="text" value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} placeholder="MP4 URL..." />
                        <button type="button" className="btn btn-xs mt-xs" onClick={() => handleEditFileSelect('video')}>Upload Video</button>
                      </div>
                      <div className="form-group">
                        <label>Video Thumbnail</label>
                        <DragDropImageUpload
                          type="thumbnail"
                          uploading={editUploading}
                          imageUrl={form.thumbnail_url}
                          onFileSelect={handleEditFileSelect}
                        />
                        <input type="text" value={form.thumbnail_url} onChange={e => setForm(f => ({ ...f, thumbnail_url: e.target.value }))} placeholder="Or paste thumbnail URL" className="mt-sm" />
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <div className="flex-row justify-between items-center mb-1">
                      <label className="mb-0">Title <span className="char-count">{(form.title || '').length}/75</span></label>
                    </div>
                    <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value.slice(0, 75) }))} maxLength={75} required />
                  </div>
                  <div className="form-group">
                    <div className="flex-row justify-between items-center mb-1">
                      <label className="mb-0">Matter <span className="char-count">{(form.matter || '').length}/400</span></label>
                    </div>
                    <textarea rows={6} value={form.matter} onChange={(e) => setForm((f) => ({ ...f, matter: e.target.value.slice(0, 400) }))} maxLength={400} required />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                      <option value="">Select</option>
                      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Sub Category</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minHeight: '42px' }}>
                      {['ఆంధ్రప్రదేశ్', 'తెలంగాణ', ALL_INDIA_STATE + ' ', INTERNATIONAL_STATE].map(stateName => {
                        const isSelected = form.state && statesMatch(form.state, stateName);
                        return (
                          <label key={stateName} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#EF233C' : '#333', fontWeight: isSelected ? '500' : '400' }}>
                            <input
                              type="radio"
                              name="edit_quick_state"
                              checked={isSelected}
                              onChange={() => setForm(f => ({ ...f, state: stateName, city: '' }))}
                              style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#EF233C' }}
                            />
                            {stateName.trim()}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <select value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}>
                      <option value="">Select City</option>
                      {cities.filter((c) => !form.state || statesMatch(c.state, form.state)).map((c) => (
                        <option key={c.id || c.name} value={c.name}>{c.name}</option>
                      ))}
                      {form.city && !cities.some(c => c.name === form.city) && (
                        <option value={form.city}>{form.city}</option>
                      )}
                    </select>
                  </div>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input type="checkbox" checked={!!form.is_breaking} onChange={(e) => setForm((f) => ({ ...f, is_breaking: e.target.checked }))} />
                      <span className="breaking-tag">Breaking News</span>
                    </label>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked }))} />
                      <span className="featured-tag">Featured Article</span>
                    </label>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input type="checkbox" checked={!!form.zone_only} onChange={(e) => setForm((f) => ({ ...f, zone_only: e.target.checked }))} />
                      <span className="zone-tag" style={{ background: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Show only in zone</span>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: (current.media_type === 'video' && current.video_url) || current.image_url ? '340px 1fr' : '1fr',
                    gap: 24,
                    alignItems: 'start',
                  }}>
                    {(current.media_type === 'video' && current.video_url) ? (
                      <video
                        src={current.video_url}
                        poster={current.thumbnail_url}
                        controls
                        className="detail-img"
                        style={{ objectFit: 'contain', background: '#000', maxHeight: '320px', width: '100%' }}
                      />
                    ) : current.image_url ? (
                      <img src={current.image_url} alt="" className="detail-img" style={{ maxHeight: '320px', width: '100%', objectFit: 'cover' }} />
                    ) : null}

                    <div className="detail-row">
                      <span className="detail-label">
                        Matter {savingField === 'matter' && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(Saving…)</span>}
                      </span>
                      <textarea
                        ref={matterRef}
                        value={matterDraft}
                        onChange={e => setMatterDraft(e.target.value.slice(0, 400))}
                        onBlur={() => saveInlineField('matter', matterDraft)}
                        disabled={current.status === 'accepted'}
                        maxLength={400}
                        placeholder="Not Available"
                        rows={1}
                        className="inline-edit-field cr-matter-text"
                        style={{ whiteSpace: 'pre-wrap', fontSize: '20px', width: '100%', display: 'block', resize: 'none', overflow: 'hidden', fontFamily: 'inherit' }}
                      />
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 2 }}>{matterDraft.length}/400</div>

                      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginTop: 12 }}>
                        <label className="checkbox-label" style={{ margin: 0, opacity: current.status === 'accepted' ? 0.6 : 1 }}>
                          <input type="checkbox" checked={!!current.is_breaking} disabled={current.status === 'accepted'} onChange={() => toggleDetailFlag('is_breaking')} />
                          <span className="breaking-tag">Breaking News</span>
                        </label>
                        <label className="checkbox-label" style={{ margin: 0, opacity: current.status === 'accepted' ? 0.6 : 1 }}>
                          <input type="checkbox" checked={!!current.is_featured} disabled={current.status === 'accepted'} onChange={() => toggleDetailFlag('is_featured')} />
                          <span className="featured-tag">Featured Article</span>
                        </label>
                        <label className="checkbox-label" style={{ margin: 0, opacity: current.status === 'accepted' ? 0.6 : 1 }}>
                          <input type="checkbox" checked={!!current.zone_only} disabled={current.status === 'accepted'} onChange={() => toggleDetailFlag('zone_only')} />
                          <span className="zone-tag" style={{ background: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Show only in zone</span>
                        </label>
                        {current.status === 'accepted' && (
                          <span className="text-muted" style={{ fontSize: 12 }}>Already published — edit the article directly to change these.</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {current.status === 'accepted' && (
                    <div className="detail-row" style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <span className="detail-label">Earning credited</span>
                        <span style={{ fontWeight: 600 }}>{formatINR(current.earning_amount)}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-xs btn-ghost"
                        onClick={() => handlePreAccept(current)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}
                      >
                        <MdEdit size={14} /> Change Worth
                      </button>
                    </div>
                  )}
                  {current.status === 'rejected' && current.rejection_reason && (
                    <div className="detail-row" style={{ marginTop: 16 }}><span className="detail-label">Rejection reason</span><span>{current.rejection_reason}</span></div>
                  )}
                </>
              )}
            </div>

            {current.status === 'pending' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                {editing ? (
                  <>
                    <button className="btn btn-ghost" disabled={busy} onClick={() => setEditing(false)}>Cancel Edit</button>
                    <button className="btn btn-primary" disabled={busy} onClick={saveEdit}>{busy ? 'Saving…' : 'Save Changes'}</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-ghost" onClick={() => startEdit(current)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => setRejectFor(current)}><MdCancel /> Reject</button>
                    <button className="btn btn-primary" disabled={checkingDuplicate || busy} onClick={() => handleAcceptClick(current)}>
                      {checkingDuplicate ? 'Checking duplicates...' : busy ? 'Accepting…' : <><MdCheckCircle /> {quickApprove ? `Accept ₹${defaultAmount || 0}` : 'Accept'}</>}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          )}
        </>
      )}

      {/* Accept / Change Worth modal */}
      {acceptFor && (
        <div className="modal-overlay" onClick={() => setAcceptFor(null)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{acceptFor.status === 'accepted' ? 'Change Article Worth' : 'Accept & Credit Contributor'}</h3>
              <button className="modal-close" onClick={() => setAcceptFor(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p className="text-muted mb-sm">"{acceptFor.title}"</p>
              {acceptFor.status === 'accepted' && (
                <div style={{ background: '#F9FAFB', padding: '12px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13, border: '1px solid #E5E7EB' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Previous Worth:</span>
                    <strong>{formatINR(acceptFor.earning_amount)}</strong>
                  </div>
                  {amount !== '' && !isNaN(Number(amount)) && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>New Worth:</span>
                        <strong>{formatINR(Number(amount))}</strong>
                      </div>
                      <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        {Number(amount) > Number(acceptFor.earning_amount) ? (
                          <>
                            <span style={{ color: '#059669' }}>Additional Credit:</span>
                            <span style={{ color: '#059669' }}>+{formatINR(Number(amount) - Number(acceptFor.earning_amount))}</span>
                          </>
                        ) : Number(amount) < Number(acceptFor.earning_amount) ? (
                          <>
                            <span style={{ color: '#4B5563' }}>Wallet debit:</span>
                            <span style={{ color: '#4B5563' }}>₹0</span>
                          </>
                        ) : (
                          <>
                            <span style={{ color: '#6B7280' }}>Adjustment:</span>
                            <span style={{ color: '#6B7280' }}>₹0 (No change)</span>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="form-group">
                <label>{acceptFor.status === 'accepted' ? 'New Article Worth (₹)' : 'Earning Amount (₹)'}</label>
                <input type="number" min="0" value={amount} placeholder="e.g. 10" onChange={(e) => setAmount(e.target.value)} autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAcceptFor(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={accept} disabled={busy || amount === ''}>
                {busy ? 'Saving…' : acceptFor.status === 'accepted' ? 'Update Worth' : 'Accept & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectFor && (
        <ReasonModal
          title="Reject Submission"
          reasons={reasons}
          confirmLabel="Reject Submission"
          busy={busy}
          onCancel={() => setRejectFor(null)}
          onConfirm={reject}
        />
      )}

      {bulkRejectOpen && (
        <ReasonModal
          title={`Reject ${selectedIds.length} selected article${selectedIds.length > 1 ? 's' : ''}?`}
          description="This will reject all selected Contributor submissions."
          reasons={reasons}
          confirmLabel={`Reject ${selectedIds.length} Article${selectedIds.length > 1 ? 's' : ''}`}
          confirmClass="btn-danger"
          busy={busy}
          onCancel={() => setBulkRejectOpen(false)}
          onConfirm={handleBulkReject}
        />
      )}

      {/* Duplicate Warning Modal */}
      {duplicateWarning && (
        <div className="modal-overlay" onClick={() => setDuplicateWarning(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ background: '#FEF2F2', padding: '20px 24px', borderBottom: '1px solid #FEE2E2', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>Duplicate Article Found</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#B91C1C', marginTop: 2 }}>
                  This contributor submission is very similar to an existing published article.
                </p>
              </div>
              <button className="modal-close" onClick={() => setDuplicateWarning(null)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                {duplicateWarning.duplicates.length} similar article{duplicateWarning.duplicates.length > 1 ? 's' : ''} found — review before accepting.
              </p>

              {duplicateWarning.duplicates.map((dup) => (
                <div key={dup.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {dup.ai_reason ? 'AI Confidence' : 'Similarity Score'}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#E11D48', padding: '2px 10px', background: '#FFE4E6', borderRadius: 20 }}>
                      {dup.ai_reason ? `${Math.round(dup.ai_confidence * 100)}%` : `${dup.score}%`}
                    </span>
                  </div>

                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6, lineHeight: 1.4 }}>
                    {dup.title}
                  </div>

                  {dup.ai_reason && (
                    <p style={{ fontSize: 13, color: '#78350F', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                      {dup.ai_reason}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    <div><strong>Reporter:</strong> {dup.reporter_name || 'Admin'}</div>
                    <div><strong>Category:</strong> {dup.category}</div>
                    <div><strong>Published Date:</strong> {new Date(dup.published_at).toLocaleDateString()}</div>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: '#0284C7', color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setViewMatchedArticle(dup)}
                  >
                    <MdVisibility size={14} /> View Matched Article
                  </button>
                </div>
              ))}
            </div>

            <div className="modal-footer" style={{ padding: '16px 24px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button className="btn btn-ghost" onClick={() => setDuplicateWarning(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const sub = duplicateWarning.submission;
                  setDuplicateWarning(null);
                  setAcceptFor(sub);
                  setAmount('');
                }}
              >
                Proceed & Accept Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Matched Article Modal */}
      {viewMatchedArticle && (
        <div className="modal-overlay" onClick={() => setViewMatchedArticle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, borderRadius: 16 }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Published Article Preview</h3>
              <button className="modal-close" onClick={() => setViewMatchedArticle(null)}><MdClose /></button>
            </div>
            <div className="modal-body" style={{ padding: 24 }}>
              {viewMatchedArticle.image_url && (
                <img src={viewMatchedArticle.image_url} alt="" style={{ width: '100%', borderRadius: 12, marginBottom: 16, maxHeight: 250, objectFit: 'cover' }} />
              )}
              <h4 style={{ marginTop: 0, marginBottom: 8, fontSize: 18 }}>{viewMatchedArticle.title}</h4>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <span className="badge badge-category">{viewMatchedArticle.category}</span>
                <span className="badge badge-neutral">{new Date(viewMatchedArticle.published_at).toLocaleDateString()}</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{viewMatchedArticle.summary}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setViewMatchedArticle(null)}>Close Preview</button>
            </div>
          </div>
        </div>
      )}

      {editCropper.active && (
        <ImageCropper
          image={editCropper.image}
          aspectRatio={editCropper.type === 'thumbnail' ? 300 / 169 : 720 / 590}
          onCropComplete={handleEditCropComplete}
          onCancel={() => setEditCropper({ active: false, image: null, type: null })}
        />
      )}

      {showWebPreview && current && (
        <WebPreviewModal
          article={{ ...current, summary: current.matter, published_at: current.submitted_at }}
          onClose={() => setShowWebPreview(false)}
        />
      )}
    </div>
  );
}
